/**
 * Profile Photos — transport, normalizer, and adapter for the
 * B-2.B durable profile-photo endpoints.
 *
 * Contract authority:
 *   - origin/act/b2-b-profile-media-backend-correction01
 *     @ aff44b9088838fa70874b0874a773c88ba2f1746
 *   - docs/epics/profile-questionnaire-detail-slice-b-2/
 *       b2-b-media-contract01-correction01.md
 *   - docs/epics/profile-questionnaire-detail-slice-b-2/
 *       b2-b-media-contract01.md
 *   - api/openapi/paths/profile_media.yaml
 *   - api/openapi/schemas/profile_media.yaml
 *   - docs/epics/profile-questionnaire-detail-slice-b-2/
 *       b2-b-frontend-media-contract01.md  (FE consumption notes)
 *
 * Per ACT-INDEEP-B2-B-PROFILE-MEDIA-FRONTEND01 §1, §5, §7, §8, §10:
 *
 *   PHOTO_MAX_COUNT     = 6
 *   PHOTO_MAX_BYTES     = 8 MiB
 *   UPLOAD_ENCODING     = multipart/form-data
 *   FILE_PART_NAME      = "file"
 *   CANONICAL_CONTENT_URL
 *                       = /api/v1/profile-photos/{photoID}/content
 *                         (role-neutral; NEVER reconstructed)
 *
 * The FE never reconstructs role-prefixed content URLs from photo
 * IDs. The canonical URL is consumed verbatim from the server
 * response.
 *
 * This module is the single source of truth for the `ProfilePhoto`
 * transport type and normalizer; questionnaire / my-profile /
 * profile-detail all consume it via the exported helpers.
 */

import { getCSRFToken, CSRF_HEADER_NAME } from './auth';

// =============================================================================
// Frozen wire constants (mirrored from backend contract authority)
// =============================================================================

/** Maximum number of durable photos per profile (server-enforced). */
export const PHOTO_MAX_COUNT = 6 as const;

/** Maximum per-file size in bytes (8 MiB). */
export const PHOTO_MAX_BYTES: number = 8 * 1024 * 1024;

/** The canonical multipart part name expected by the backend. */
export const FILE_PART_NAME = 'file' as const;

/** Allowed browser-facing MIME types (FE client preflight only). */
export const ACCEPTED_PHOTO_MIME_TYPES: readonly string[] = Object.freeze([
  'image/jpeg',
  'image/png',
  'image/webp',
]);

/**
 * Server-relative canonical content URL prefix. The photo ID is
 * appended by the server response builder; the FE MUST NOT build
 * role-prefixed variants (`/api/v1/friend/profile/photos/{id}/content`
 * or `/api/v1/sponsor/profile/photos/{id}/content`) locally.
 */
export const CANONICAL_CONTENT_URL_PREFIX =
  '/api/v1/profile-photos/' as const;

/** Suffix for the canonical content URL (role-neutral). */
export const CANONICAL_CONTENT_URL_SUFFIX = '/content' as const;

// =============================================================================
// Transport DTOs
// =============================================================================

/**
 * Canonical photo summary. Returned in upload + delete responses
 * and embedded in profile / discovery DTOs.
 *
 * Identity-minimized: no storage_key, no filesystem path, no
 * original filename. The URL is server-relative and constructed by
 * the response builder, not persisted.
 */
export interface ProfilePhoto {
  /** Canonical photo identifier (UUID v4). Server-generated. */
  readonly id: string;
  /**
   * Application-managed content URL for the photo bytes.
   * Format: /api/v1/profile-photos/{photoID}/content
   * (role-neutral; authorization is per backend contract §C1).
   * The FE MUST consume this verbatim.
   */
  readonly url: string;
  /**
   * Zero-based insertion-order position. Compacted 0..N-1 on delete.
   * The first photo by position is the canonical primary projection
   * (no user-selected primary).
   */
  readonly position: number;
}

/**
 * Response envelope for upload + delete operations. Always returns
 * the canonical current photo list after the operation.
 */
export interface ProfilePhotosResponse {
  readonly photos: readonly ProfilePhoto[];
}

// =============================================================================
// Strict normalizer (single source of truth)
// =============================================================================

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isUuidLike(value: string): boolean {
  return UUID_RE.test(value);
}

/**
 * Strictly normalize a single photo candidate. Returns null for
 * unusable entries; never fabricates fields. Violations are
 * reported as a parallel list so the reducer can surface them via
 * the visible serverError (contract drift is loud, never silent).
 */
export function normalizeProfilePhoto(
  input: unknown,
  index: number
): { readonly photo: ProfilePhoto | null; readonly violation: string | null } {
  const violation = (reason: string): string =>
    `photos[${index}]: ${reason}`;
  if (input === null || typeof input !== 'object') {
    return { photo: null, violation: violation('not an object') };
  }
  const obj = input as Record<string, unknown>;

  if (typeof obj.id !== 'string' || obj.id.length === 0) {
    return { photo: null, violation: violation('id missing or not string') };
  }
  if (!isUuidLike(obj.id)) {
    return {
      photo: null,
      violation: violation(`id is not UUID-shaped: ${obj.id}`),
    };
  }
  if (typeof obj.url !== 'string' || obj.url.length === 0) {
    return { photo: null, violation: violation('url missing or not string') };
  }
  const expectedPrefix = CANONICAL_CONTENT_URL_PREFIX;
  const expectedSuffix = CANONICAL_CONTENT_URL_SUFFIX;
  if (!obj.url.startsWith(expectedPrefix) || !obj.url.endsWith(expectedSuffix)) {
    return {
      photo: null,
      violation: violation(
        `url is not the canonical role-neutral content URL: ${obj.url}`
      ),
    };
  }
  if (typeof obj.position !== 'number' || !Number.isInteger(obj.position)) {
    return {
      photo: null,
      violation: violation('position missing or not integer'),
    };
  }
  if (obj.position < 0) {
    return {
      photo: null,
      violation: violation(`negative position: ${obj.position}`),
    };
  }
  if (obj.position >= PHOTO_MAX_COUNT) {
    return {
      photo: null,
      violation: violation(
        `position >= PHOTO_MAX_COUNT (${PHOTO_MAX_COUNT}): ${obj.position}`
      ),
    };
  }
  const photo: ProfilePhoto = {
    id: obj.id,
    url: obj.url,
    position: obj.position,
  };
  return { photo, violation: null };
}

function normalizeAndSort(
  input: readonly unknown[],
  violations: string[]
): readonly ProfilePhoto[] {
  const valid: ProfilePhoto[] = [];
  input.forEach((entry, index) => {
    const { photo, violation } = normalizeProfilePhoto(entry, index);
    if (photo !== null) {
      valid.push(photo);
    }
    if (violation !== null) {
      violations.push(violation);
    }
  });
  valid.sort((a, b) => {
    if (a.position !== b.position) return a.position - b.position;
    return a.id.localeCompare(b.id);
  });
  return valid;
}

/**
 * Normalize a photo-list response from the server. Returns the
 * canonical photo list (sorted by position ascending) and a list of
 * violations for malformed entries. The caller decides what to do
 * with violations (typically surface them via `serverError`).
 */
export function normalizeProfilePhotos(input: unknown): {
  readonly photos: readonly ProfilePhoto[];
  readonly violations: readonly string[];
} {
  if (input === undefined || input === null) {
    return { photos: [], violations: [] };
  }
  if (!Array.isArray(input)) {
    return {
      photos: [],
      violations: [`photos: expected array, got ${typeof input}`],
    };
  }
  if (input.length > PHOTO_MAX_COUNT) {
    const violations: string[] = [
      `photos: server returned ${input.length} > PHOTO_MAX_COUNT (${PHOTO_MAX_COUNT})`,
    ];
    const trimmed = input.slice(0, PHOTO_MAX_COUNT);
    return { photos: normalizeAndSort(trimmed, violations), violations };
  }
  const violations: string[] = [];
  return { photos: normalizeAndSort(input, violations), violations };
}

/**
 * Normalize the `photos` field on a profile response. Treats
 * absence / null as "no photos" (canonical empty state).
 */
export function normalizeProfilePhotosField(input: unknown): {
  readonly photos: readonly ProfilePhoto[];
  readonly violations: readonly string[];
} {
  return normalizeProfilePhotos(input);
}

// =============================================================================
// Client-side preflight (UX only; server remains authority)
// =============================================================================

export interface ClientPreflightRejection {
  readonly reason:
    | 'file_too_large'
    | 'unsupported_format'
    | 'no_file';
}

/**
 * Convenience-only check. The server is still authoritative; see
 * backend contract §9 (server sniffs bytes via
 * http.DetectContentType and rejects mismatches).
 *
 * `file.type` is browser-reported and may be missing or wrong on
 * some browsers; we accept it as a hint and only reject when it
 * is present-and-mismatched. Missing type is OK (the server sniffs).
 */
export function clientPreflightPhotoFile(
  file: File | Blob | null | undefined
): ClientPreflightRejection | null {
  if (!file) return { reason: 'no_file' };
  const size = (file as File).size;
  if (typeof size === 'number' && size > PHOTO_MAX_BYTES) {
    return { reason: 'file_too_large' };
  }
  const type = (file as File).type;
  if (typeof type === 'string' && type.length > 0) {
    if (!ACCEPTED_PHOTO_MIME_TYPES.includes(type)) {
      return { reason: 'unsupported_format' };
    }
  }
  return null;
}

// =============================================================================
// Error taxonomy
// =============================================================================

export type UploadPhotoErrorCode =
  | 'generic_upload_failure'
  | 'upload_requires_login'
  | 'maximum_six_photos'
  | 'file_too_large'
  | 'unsupported_format'
  | 'temporary_unavailable'
  | 'malformed_server_response';

export class UploadPhotoError extends Error {
  readonly code: UploadPhotoErrorCode;
  readonly statusCode: number | undefined;
  constructor(
    message: string,
    code: UploadPhotoErrorCode,
    statusCode?: number
  ) {
    super(message);
    this.name = 'UploadPhotoError';
    this.code = code;
    this.statusCode = statusCode;
  }
}

export type DeletePhotoErrorCode =
  | 'generic_delete_failure'
  | 'delete_requires_login'
  | 'delete_target_unavailable'
  | 'temporary_unavailable'
  | 'malformed_server_response';

export class DeletePhotoError extends Error {
  readonly code: DeletePhotoErrorCode;
  readonly statusCode: number | undefined;
  constructor(
    message: string,
    code: DeletePhotoErrorCode,
    statusCode?: number
  ) {
    super(message);
    this.name = 'DeletePhotoError';
    this.code = code;
    this.statusCode = statusCode;
  }
}

// =============================================================================
// Internal: low-level authenticated fetch (no Content-Type for multipart)
// =============================================================================

const API_BASE = '/api/v1';

async function postPhoto(
  path: string,
  body: FormData
): Promise<ProfilePhotosResponse> {
  // ACT-INDEEP-PRODUCTION-FEED-CSRF-SESSION-CONTRACT01 (CORRECTION01):
  // profilePhotos.ts uses the synchronous `getCSRFToken()` (cookie-only
  // read). The defensive `GET /api/auth/csrf` bootstrap is scoped to
  // feed-posts in this ACT. profilePhotos has NOT migrated to the
  // `await ensureCSRFToken()` pattern; if it surfaces a similar
  // production defect (cookie absent OR cookie carries a consumed
  // token because the backend's CSRFStore is single-use), see P2-01
  // cross-adapter backlog item.
  const csrfToken = getCSRFToken();
  const response = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    // No Content-Type header — the browser sets multipart/form-data
    // with the correct boundary parameter automatically.
    headers: {
      ...(csrfToken ? { [CSRF_HEADER_NAME]: csrfToken } : {}),
    },
    credentials: 'include',
    body,
  });
  return parsePhotosResponse(response);
}

async function deletePhoto(path: string): Promise<ProfilePhotosResponse> {
  // ACT-INDEEP-PRODUCTION-FEED-CSRF-SESSION-CONTRACT01 (CORRECTION01):
  // Same comment as `postPhoto`: profilePhotos.ts follows the
  // synchronous cookie-only contract; the per-mutation
  // `GET /api/auth/csrf` bootstrap is scoped to feed-posts. See
  // P2-01 cross-adapter backlog item.
  const csrfToken = getCSRFToken();
  const response = await fetch(`${API_BASE}${path}`, {
    method: 'DELETE',
    headers: {
      ...(csrfToken ? { [CSRF_HEADER_NAME]: csrfToken } : {}),
    },
    credentials: 'include',
  });
  return parsePhotosResponse(response);
}

async function parsePhotosResponse(
  response: Response
): Promise<ProfilePhotosResponse> {
  let raw: unknown = null;
  try {
    raw = await response.json();
  } catch {
    if (response.ok) {
      throw new UploadPhotoError(
        'malformed JSON',
        'malformed_server_response',
        response.status
      );
    }
    throw uploadOrDeleteError(response);
  }
  if (!response.ok) {
    throw uploadOrDeleteError(response);
  }
  if (raw === null || typeof raw !== 'object') {
    throw new UploadPhotoError(
      'response is not an object',
      'malformed_server_response',
      response.status
    );
  }
  const obj = raw as { photos?: unknown; error?: unknown };
  const normalized = normalizeProfilePhotos(obj.photos);
  if (normalized.violations.length > 0) {
    throw new UploadPhotoError(
      normalized.violations.join('; '),
      'malformed_server_response',
      response.status
    );
  }
  return { photos: normalized.photos };
}

function uploadOrDeleteError(response: Response): UploadPhotoError | DeletePhotoError {
  const status = response.status;
  const message = response.statusText || 'photo request failed';
  if (status === 404) {
    return new DeletePhotoError(message, 'delete_target_unavailable', status);
  }
  return new UploadPhotoError(message, uploadCodeForStatus(status), status);
}

function uploadCodeForStatus(status: number): UploadPhotoErrorCode {
  if (status === 400) return 'generic_upload_failure';
  if (status === 401) return 'upload_requires_login';
  if (status === 403) return 'generic_upload_failure';
  if (status === 409) return 'maximum_six_photos';
  if (status === 413) return 'file_too_large';
  if (status === 415) return 'unsupported_format';
  if (status === 503 || status >= 500) return 'temporary_unavailable';
  return 'generic_upload_failure';
}

// =============================================================================
// Public API adapters
// =============================================================================

/**
 * Upload a profile photo for the authenticated actor's friend profile.
 */
export async function uploadFriendProfilePhoto(
  file: File
): Promise<ProfilePhotosResponse> {
  const body = new FormData();
  body.append(FILE_PART_NAME, file);
  return postPhoto('/friend/profile/photos', body);
}

/**
 * Upload a profile photo for the authenticated actor's sponsor profile.
 */
export async function uploadSponsorProfilePhoto(
  file: File
): Promise<ProfilePhotosResponse> {
  const body = new FormData();
  body.append(FILE_PART_NAME, file);
  return postPhoto('/sponsor/profile/photos', body);
}

/**
 * Delete a profile photo by its canonical photo UUID.
 *
 * The backend enforces owner-only authorization (BOLA-safe; 404 for
 * other users). The photo UUID alone is NOT authorization; the
 * server independently authorizes.
 */
export async function deleteFriendProfilePhoto(
  photoID: string
): Promise<ProfilePhotosResponse> {
  if (typeof photoID !== 'string' || photoID.length === 0) {
    throw new DeletePhotoError(
      'photoID required',
      'generic_delete_failure'
    );
  }
  return deletePhoto(`/friend/profile/photos/${encodeURIComponent(photoID)}`);
}

export async function deleteSponsorProfilePhoto(
  photoID: string
): Promise<ProfilePhotosResponse> {
  if (typeof photoID !== 'string' || photoID.length === 0) {
    throw new DeletePhotoError(
      'photoID required',
      'generic_delete_failure'
    );
  }
  return deletePhoto(`/sponsor/profile/photos/${encodeURIComponent(photoID)}`);
}

// =============================================================================
// Re-exports (single source of truth for cross-surface consumers)
// =============================================================================

export type {
  ProfilePhoto as ProfilePhotoDto,
  ProfilePhotosResponse as ProfilePhotosResponseDto,
};
